/*
  VOLVIK STORE PRODUCT CARD
  =========================
  Ignite already owns:
  - ProductCard custom element
  - swatch hover/touch previews
  - quick add / quick view
  - AJAX product forms
  - collection facet replacements

  This file only adds the persistent swatch selection behavior used by the
  Custom Featured Collection card so Collection looks/behaves the same.
*/

(() => {
  const ROOT_SELECTOR = '.volvik-store-product-cards';
  const CARD_SELECTOR = 'product-card';
  const SWATCH_SELECTOR = '.card-swatch-container button.swatch';
  const ACTIVE_CLASS = 'is-swatch-preview-active';

  function getPrimaryImage(card) {
    if (!card) return null;

    return (
      Array.from(card.querySelectorAll('.card__media .media img')).find(
        (image) =>
          !image.classList.contains('swatch-hover-image') &&
          !image.classList.contains('card__image-hover')
      ) || null
    );
  }

  function setSelectedSwatch(swatch, changeImage = true) {
    const card = swatch?.closest(CARD_SELECTOR);

    if (!card) return;

    card.querySelectorAll(SWATCH_SELECTOR).forEach((item) => {
      const selected = item === swatch;

      item.classList.toggle('is-selected', selected);
      item.setAttribute(
        'aria-pressed',
        selected ? 'true' : 'false'
      );
    });

    if (!changeImage) return;

    const imageUrl = swatch.dataset.variantMedia;
    const primaryImage = getPrimaryImage(card);

    if (!imageUrl || !primaryImage) return;

    primaryImage.src = imageUrl;
    primaryImage.srcset = imageUrl;

    card.classList.add(ACTIVE_CLASS);
  }

  function initializeCard(card) {
    if (
      !card ||
      card.dataset.volvikStoreCardReady === 'true'
    ) {
      return;
    }

    const swatches = Array.from(
      card.querySelectorAll(SWATCH_SELECTOR)
    );

    if (!swatches.length) {
      card.dataset.volvikStoreCardReady = 'true';
      return;
    }

    card.dataset.volvikStoreCardReady = 'true';

    swatches.forEach((swatch) => {
      if (!swatch.hasAttribute('aria-pressed')) {
        swatch.setAttribute(
          'aria-pressed',
          'false'
        );
      }
    });

    const selected = swatches.find(
      (swatch) =>
        swatch.classList.contains('is-selected') ||
        swatch.getAttribute('aria-pressed') === 'true'
    );

    if (!selected) {
      setSelectedSwatch(
        swatches[0],
        false
      );
    }

    /*
      When the pointer leaves the card we remove only our handoff class.

      Ignite still owns its native hover behavior and secondary image logic.
    */
    card.addEventListener(
      'mouseleave',
      () => {
        if (
          !card.classList.contains(ACTIVE_CLASS)
        ) {
          return;
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            card.classList.remove(
              ACTIVE_CLASS
            );
          });
        });
      },
      { passive: true }
    );
  }

  function initializeRoot(root) {
    if (!root) return;

    if (root.matches?.(CARD_SELECTOR)) {
      initializeCard(root);
    }

    root
      .querySelectorAll?.(CARD_SELECTOR)
      .forEach(initializeCard);
  }

  function initializeExistingCards() {
    document
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(initializeRoot);
  }

  /*
    Delegated click survives Shopify's AJAX collection updates.

    Clicking a swatch:
    - keeps Ignite's own swatch interaction intact
    - adds our persistent selected state
    - changes the main product-card image
  */
  document.addEventListener(
    'click',
    (event) => {
      const swatch = event.target.closest(
        `${ROOT_SELECTOR} ${SWATCH_SELECTOR}`
      );

      if (!swatch) return;

      setSelectedSwatch(
        swatch,
        true
      );
    }
  );

  /*
    Shopify collection filters and infinite/paginated rendering can replace
    the grid without doing a full page refresh.

    Observe newly inserted nodes and initialize only cards that belong to
    our Volvik product-card scope.
  */
  const observer = new MutationObserver(
    (mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) {
            return;
          }

          if (
            node.matches(ROOT_SELECTOR)
          ) {
            initializeRoot(node);
            return;
          }

          node
            .querySelectorAll?.(
              ROOT_SELECTOR
            )
            .forEach(initializeRoot);

          const root =
            node.closest?.(
              ROOT_SELECTOR
            );

          if (root) {
            initializeRoot(root);
          }
        });
      });
    }
  );

  function start() {
    initializeExistingCards();

    observer.observe(
      document.documentElement,
      {
        childList: true,
        subtree: true,
      }
    );
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }

  /*
    Shopify Theme Editor support.
  */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      const root =
        event.target.querySelector?.(
          ROOT_SELECTOR
        );

      if (root) {
        initializeRoot(root);
      }
    }
  );
})();